/**
 * Chapter key normalization and Nova right-rail copy for demo / dashboard UIs.
 */

export function normalizeDemoChapterKey(ch) {
  const x = String(ch || 'fertility').toLowerCase();
  if (x === 'first-chapter' || x === 'cycle' || x === 'trying-to-conceive') return 'ttc';
  if (x === 'motherhood' || x === 'menopause') return 'postpartum';
  return ['fertility', 'ttc', 'pregnancy', 'postpartum'].includes(x) ? x : 'fertility';
}

/** Nova right-rail cards — fertility is the current detailed default; other chapters get parallel structure */
export function getNovaSidebarCopy(key) {
  const k = normalizeDemoChapterKey(key);
  const common = {
    novaSubline: 'Watching over your journey',
    askChips: ['What should I ask at my next visit?', 'What does this symptom mean?', "I'm anxious today — talk me through it"],
  };

  const by = {
    fertility: {
      ...common,
      headsUpTitle: 'Heads up for tomorrow',
      headsUpBody:
        "Your monitoring scan is tomorrow morning. They'll check follicle size and estrogen. Based on your cycle 1 data, day 9 is usually when things get interesting for you.",
      tipTitle: 'Staying hydrated during stims matters more than you think',
      tipBody: 'High follicle counts increase your OHSS risk. 2–3L of electrolyte fluid from day 6.',
      tipChip: 'Explain OHSS risk and hydration during stims',
      articleTitle: 'What your estrogen level tells you on day 8',
      articleMeta: 'CCRM · 4 min read',
      womenSectionTitle: 'Women like you right now',
      womenName: 'Jamie',
      womenBody: 'IVF cycle 2 · low AMH · Columbus, Ohio · retrieval same week as you',
      womenLink: 'Connect →',
      eventTitle: 'IVF Support Circle — Columbus, Ohio · Sat Mar 29',
      eventMeta: 'Hosted by RESOLVE · Ohio · Free',
      reminderTitle: "Today's reminder",
      quoteBody: 'You are not behind. You are not failing. You are doing one of the hardest things a person can do.',
      quoteAttribution: '— Shared by 847 women in the fertility chapter',
      askChips: ["What should I ask at tomorrow's scan?", 'What does my follicle count mean?', "I'm feeling anxious about my results"],
    },
    ttc: {
      ...common,
      headsUpTitle: 'Timing window',
      headsUpBody:
        'Your LH pattern suggests the next 48 hours matter for timing. Nova can line up questions for your partner or clinic.',
      tipTitle: 'Hydration & cervical fluid',
      tipBody: 'Mild dehydration can make fluid harder to read. Small, steady water beats chugging right before tests.',
      tipChip: 'Explain LH surges vs. peak',
      articleTitle: 'When to call after 6 months of trying',
      articleMeta: 'ASRM patient guide · 6 min read',
      womenSectionTitle: 'Women on a similar timeline',
      womenName: 'Riley',
      womenBody: 'TTC month 5 · OPK logging · same clinic network as you',
      womenLink: 'Connect →',
      eventTitle: 'TTC circle — Columbus · Sun 4pm',
      eventMeta: 'Facilitated · Donation optional',
      reminderTitle: 'One thing to remember',
      quoteBody: 'You’re allowed to protect your hope and your boundaries at the same time.',
      quoteAttribution: '— Shared by 620 women in the TTC chapter',
      askChips: ['Is my timing off this month?', 'What to log before my appointment?', 'How do I handle the TWW?'],
    },
    pregnancy: {
      ...common,
      headsUpTitle: 'Up next',
      headsUpBody:
        'Anatomy scan is coming up — Nova saved a short list of optional questions if you want them in your pocket.',
      tipTitle: 'When swelling is worth a call',
      tipBody: 'Sudden face or hand swelling with headache or vision changes beats “normal third trimester” — Nova can help you phrase it for triage.',
      tipChip: 'Red flags in pregnancy — in plain language',
      articleTitle: 'Kick counts: how and when',
      articleMeta: 'March of Dimes · 4 min read',
      womenSectionTitle: 'Due around the same time',
      womenName: 'Morgan',
      womenBody: 'Week 22 · first baby · also navigating anxiety between visits',
      womenLink: 'Connect →',
      eventTitle: 'Birth plan workshop — online · Wed 8pm',
      eventMeta: 'Partner welcome · Free',
      reminderTitle: 'Today’s reminder',
      quoteBody: 'You don’t have to love every moment to love your baby. Both can be true.',
      quoteAttribution: '— Shared by 1,100 women in the pregnancy chapter',
      askChips: ['Is this symptom normal?', 'What should I pack for birth?', 'How do I advocate in labor?'],
    },
    postpartum: {
      ...common,
      headsUpTitle: 'How you’re doing',
      headsUpBody:
        'Sleep’s been fragmented — Nova can suggest one tiny reset for today that doesn’t require “more discipline.”',
      tipTitle: 'Feeding and your wrists',
      tipBody: 'If holding feeds hurts, small positioning tweaks can help — worth mentioning at your next check-in.',
      tipChip: 'Painless feeding positions to try',
      articleTitle: 'PPD vs. baby blues: what to watch',
      articleMeta: 'Postpartum Support International · 7 min read',
      womenSectionTitle: 'Moms in the same season',
      womenName: 'Jordan',
      womenBody: '6 weeks PP · breastfeeding · also managing visitors and boundaries',
      womenLink: 'Connect →',
      eventTitle: 'Fourth trimester circle — hybrid · Sat 10am',
      eventMeta: 'Childcare not required · Free',
      reminderTitle: 'Today’s reminder',
      quoteBody: 'Recovery isn’t linear. Small wins still count as wins.',
      quoteAttribution: '— Shared by 900 women in the motherhood chapter',
      askChips: ['Is this much crying normal?', 'When to call about mood?', 'One thing for sleep tonight'],
    },
  };

  return by[k] || by.fertility;
}
